import {
  SelectSearchOptionModel,
  SelectSearchOptionOptionalModel,
} from '@/components/form/model/SelectSearchOption.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import {
  selectSearchOptionOptionalValidation,
  selectSearchOptionValidation,
} from '@/utils/validation/selectSearchOptionValidation.util.ts';
import { urlValidation } from '@/utils/validation/urlValidation.util.ts';
import { boolean, object, ObjectSchema, string } from 'yup';

// The status is recognised by its dictionary code, because its label depends on the language
export const isStudentStatus = (status?: SelectSearchOptionModel): boolean =>
  status?.code === 'STUDENT';

export interface UserPersonalDataProperties {
  aboutYourself?: string;
  areaOfInterestsUserInput?: string;
  fieldOfStudy: string;
  firstName: string;
  githubUrl?: string;
  isUnder30?: boolean;
  lastName: string;
  linkedInUrl?: string;
  location?: SelectSearchOptionModel;
  phoneNumber: string;
  studentStatus?: SelectSearchOptionModel;
  typeOfStudy?: SelectSearchOptionModel;
  university?: SelectSearchOptionOptionalModel;
  universityCustom?: string;
  yearOfStudy?: SelectSearchOptionModel;
}

export const usePersonalDataFormValidation = (): ObjectSchema<UserPersonalDataProperties> => {
  const { t } = useTranslationWithPrefix('views.validation');

  return object().shape({
    aboutYourself: string().max(1000, t('general.maxLength', { max: 1000 })),
    areaOfInterestsUserInput: string().max(500, t('general.maxLength', { max: 500 })),
    fieldOfStudy: string().required(t('general.required')),
    firstName: string().required(t('general.required')),
    githubUrl: urlValidation(t),
    isUnder30: boolean(),
    lastName: string().required(t('general.required')),
    linkedInUrl: urlValidation(t),
    location: selectSearchOptionValidation(t),
    phoneNumber: string()
      .required(t('general.required'))
      .matches(/^\+?\d{9,15}$/, t('phoneNumber.format')),
    studentStatus: selectSearchOptionValidation(t),
    typeOfStudy: selectSearchOptionValidation(t),
    university: selectSearchOptionOptionalValidation().optional(),
    universityCustom: string().optional(),
    yearOfStudy: selectSearchOptionValidation(t).when('studentStatus', {
      // An unselected status is cast to {} by yup, so it has no code
      is: (val: SelectSearchOptionModel | undefined) => isStudentStatus(val),
      otherwise: schema => schema.strip(),
      then: schema => schema.required(t('general.required')),
    }),
  });
};
